<template>
  <Layout>
    <PageHeader :title="$t('Stock Optimization')" :items="breadcrumbItems">
      <template slot="title">
        <div class="d-flex align-items-center">
          <i class="bx bx-package mr-2"></i>
          {{ $t("Stock Optimization") }}
        </div>
      </template>
    </PageHeader>

    <b-row>
      <b-col lg="12">
        <b-card>
          <!-- Period Selector -->
          <b-row class="mb-3">
            <b-col md="4">
              <label>{{ $t("Analysis Period") }}</label>
              <b-form-select
                v-model="selectedPeriod"
                :options="periodOptions"
                @change="loadData"
              />
            </b-col>
            <b-col md="8" class="d-flex align-items-end justify-content-end">
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="loadData"
                :disabled="loading"
              >
                <i class="bx bx-refresh"></i>
                {{ $t("Refresh") }}
              </b-button>
            </b-col>
          </b-row>

          <!-- Summary Cards -->
          <b-row class="mb-4">
            <b-col md="3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium">
                        {{ $t("Total Items") }}
                      </p>
                      <h4 class="mb-0">{{ summaryStats.totalItems }}</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <div
                        class="mini-stat-icon avatar-sm rounded-circle bg-primary"
                      >
                        <span class="avatar-title">
                          <i class="bx bx-package font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col md="3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium">
                        {{ $t("Need Increase") }}
                      </p>
                      <h4 class="mb-0 text-success">
                        {{ summaryStats.increaseCount }}
                      </h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <div
                        class="mini-stat-icon avatar-sm rounded-circle bg-success"
                      >
                        <span class="avatar-title">
                          <i class="bx bx-trending-up font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col md="3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium">
                        {{ $t("Need Decrease") }}
                      </p>
                      <h4 class="mb-0 text-danger">
                        {{ summaryStats.decreaseCount }}
                      </h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <div
                        class="mini-stat-icon avatar-sm rounded-circle bg-danger"
                      >
                        <span class="avatar-title">
                          <i class="bx bx-trending-down font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>

            <b-col md="3">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium">
                        {{ $t("Avg Change %") }}
                      </p>
                      <h4 class="mb-0">{{ summaryStats.avgChangePercent }}%</h4>
                    </div>
                    <div class="flex-shrink-0 align-self-center">
                      <div
                        class="mini-stat-icon avatar-sm rounded-circle bg-info"
                      >
                        <span class="avatar-title">
                          <i class="bx bx-bar-chart font-size-24"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Filters -->
          <b-row class="mb-3">
            <b-col md="3">
              <label
                >{{ $t("Search") }}
                <small class="text-muted">(% _ wildcards)</small></label
              >
              <b-form-input
                v-model="filters.search"
                :placeholder="$t('e.g. ABC% or T__-__')"
                debounce="300"
              />
            </b-col>
            <b-col md="3">
              <label>{{ $t("Supplier") }}</label>
              <b-form-select
                v-model="filters.supplierId"
                :options="supplierOptions"
              />
            </b-col>
            <b-col md="2">
              <label>{{ $t("Change Type") }}</label>
              <b-form-select
                v-model="filters.changeType"
                :options="changeTypeOptions"
              />
            </b-col>
            <b-col md="2">
              <label>{{ $t("Min Change Amount") }}</label>
              <b-form-input
                v-model.number="filters.minChangeAmount"
                type="number"
                :placeholder="$t('Minimum change...')"
              />
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col md="3" class="d-flex align-items-center">
              <b-form-checkbox v-model="filters.hideZeroMax">
                {{ $t("Hide items with max stock = 0") }}
              </b-form-checkbox>
            </b-col>
            <b-col md="9" class="d-flex align-items-center justify-content-end">
              <b-button variant="outline-secondary" @click="clearFilters">
                {{ $t("Clear Filters") }}
              </b-button>
            </b-col>
          </b-row>

          <!-- Bulk Actions Bar -->
          <b-row class="mb-3" v-if="selectedItems.size > 0">
            <b-col>
              <div
                class="alert alert-info mb-0 d-flex justify-content-between align-items-center"
              >
                <span>
                  <strong>{{ selectedItems.size }}</strong>
                  {{ $t("items selected") }}
                </span>
                <div>
                  <b-button
                    variant="outline-info"
                    size="sm"
                    class="mr-2"
                    @click="selectAllFiltered"
                  >
                    {{ $t("Select All Filtered") }}
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="mr-2"
                    @click="clearSelection"
                  >
                    {{ $t("Clear Selection") }}
                  </b-button>
                  <b-button
                    variant="success"
                    size="sm"
                    @click="confirmApplyChanges"
                  >
                    <i class="bx bx-check"></i>
                    {{ $t("Apply Selected Changes") }}
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>

          <!-- Data Table -->
          <b-table
            :items="filteredItems"
            :fields="tableFields"
            :busy="loading"
            striped
            hover
            responsive
            :per-page="perPage"
            :current-page="currentPage"
            show-empty
            :empty-text="$t('No items found')"
            :tbody-tr-class="rowClass"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>{{ $t("Loading...") }}</strong>
              </div>
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                :checked="selectedItems.has(row.item.item_id)"
                @change="toggleSelection(row.item.item_id)"
              />
            </template>

            <template #cell(item_code)="row">
              <strong>{{ row.item.item_code }}</strong>
            </template>

            <template #cell(item_description)="row">
              <div class="text-truncate" style="max-width: 300px">
                {{ row.item.item_description }}
              </div>
            </template>

            <template #cell(supplier_company_name)="row">
              <span class="text-muted">
                {{ row.item.supplier_company_name || "-" }}
              </span>
            </template>

            <template #cell(current_stock_value)="row">
              {{ formatNumber(row.item.current_stock_value) }}
            </template>

            <template #cell(stock_value_ordered)="row">
              {{ formatNumber(row.item.stock_value_ordered) }}
            </template>

            <template #cell(avg_monthly_sales)="row">
              {{ formatNumber(row.item.avg_monthly_sales, 1) }}
            </template>

            <template #cell(lead_time_days)="row">
              {{ row.item.lead_time_days }}
            </template>

            <template #cell(current_max_stock_value)="row">
              {{ formatNumber(row.item.current_max_stock_value) }}
            </template>

            <template #cell(suggested_max_stock_value)="row">
              <b-form-input
                v-model.number="row.item.suggested_max_stock_value"
                type="number"
                size="sm"
                min="0"
                style="width: 100px"
                @change="onSuggestedMaxChange(row.item)"
              />
            </template>

            <template #cell(change_amount)="row">
              <b-badge
                :variant="getChangeBadgeVariant(row.item.change_needed)"
                class="font-size-12"
              >
                {{ row.item.change_amount > 0 ? "+" : ""
                }}{{ formatNumber(row.item.change_amount) }}
              </b-badge>
            </template>

            <template #cell(current_reorder_stock_value)="row">
              {{ formatNumber(row.item.current_reorder_stock_value) }}
            </template>

            <template #cell(suggested_reorder_stock_value)="row">
              <b-form-input
                v-model.number="row.item.suggested_reorder_stock_value"
                type="number"
                size="sm"
                min="0"
                style="width: 100px"
              />
            </template>
          </b-table>

          <!-- Pagination -->
          <b-row>
            <b-col md="6" class="my-1">
              <b-form-group
                :label="$t('Per page')"
                label-cols-sm="4"
                label-cols-md="4"
                label-cols-lg="3"
                label-align-sm="left"
                label-size="sm"
                label-for="perPageSelect"
                class="mb-0"
              >
                <b-form-select
                  id="perPageSelect"
                  v-model="perPage"
                  :options="perPageOptions"
                  size="sm"
                ></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6" class="my-1">
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredItems.length"
                :per-page="perPage"
                align="right"
                size="sm"
                class="my-0"
              ></b-pagination>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Confirmation Modal -->
    <b-modal
      id="confirmModal"
      :title="$t('Confirm Stock Updates')"
      @ok="applyChanges"
      ok-variant="success"
      :ok-title="$t('Apply Changes')"
      :cancel-title="$t('Cancel')"
      size="lg"
    >
      <p>
        {{
          $t(
            "You are about to update stock values for {count} items. Please review:",
            { count: selectedItems.size }
          )
        }}
      </p>
      <div style="max-height: 400px; overflow-y: auto">
        <b-table
          :items="selectedItemsDetails"
          :fields="confirmFields"
          striped
          small
          responsive
        >
          <template #cell(current_max)="row">
            {{ formatNumber(row.item.current_max) }}
          </template>
          <template #cell(suggested_max)="row">
            <strong>{{ formatNumber(row.item.suggested_max) }}</strong>
          </template>
          <template #cell(current_reorder)="row">
            {{ formatNumber(row.item.current_reorder) }}
          </template>
          <template #cell(suggested_reorder)="row">
            <strong>{{ formatNumber(row.item.suggested_reorder) }}</strong>
          </template>
        </b-table>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "@/components/layout/app-layout";
import PageHeader from "@/components/layout/page-header";
import api from "@/helpers/api";

export default {
  name: "StockOptimization",
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      breadcrumbItems: [
        { text: this.$t("Statistics") },
        { text: this.$t("Stock Optimization"), active: true },
      ],
      loading: false,
      items: [],
      selectedItems: new Set(),
      selectedPeriod: 365,
      periodOptions: [
        { value: 30, text: this.$t("Last 30 days") },
        { value: 90, text: this.$t("Last 3 months") },
        { value: 180, text: this.$t("Last 6 months") },
        { value: 365, text: this.$t("Last 12 months") },
      ],
      filters: {
        search: "",
        changeType: "all",
        minChangeAmount: null,
        hideZeroMax: true,
        supplierId: null,
      },
      changeTypeOptions: [
        { value: "all", text: this.$t("All") },
        { value: "increase", text: this.$t("Need Increase") },
        { value: "decrease", text: this.$t("Need Decrease") },
        { value: "none", text: this.$t("No Change") },
      ],
      tableFields: [
        { key: "selected", label: "", thStyle: { width: "40px" } },
        { key: "item_code", label: this.$t("Item Code"), sortable: true },
        {
          key: "item_description",
          label: this.$t("Description"),
          sortable: true,
        },
        {
          key: "supplier_company_name",
          label: this.$t("Supplier"),
          sortable: true,
        },
        {
          key: "current_stock_value",
          label: this.$t("Current Stock"),
          sortable: true,
        },
        {
          key: "stock_value_ordered",
          label: this.$t("Ordered"),
          sortable: true,
        },
        {
          key: "avg_monthly_sales",
          label: this.$t("Avg Monthly Sales"),
          sortable: true,
        },
        {
          key: "lead_time_days",
          label: this.$t("Lead Time (days)"),
          sortable: true,
        },
        {
          key: "current_max_stock_value",
          label: this.$t("Current Max"),
          sortable: true,
        },
        {
          key: "suggested_max_stock_value",
          label: this.$t("Suggested Max"),
          sortable: true,
        },
        { key: "change_amount", label: this.$t("Change"), sortable: true },
        {
          key: "current_reorder_stock_value",
          label: this.$t("Current Reorder"),
          sortable: true,
        },
        {
          key: "suggested_reorder_stock_value",
          label: this.$t("Suggested Reorder"),
          sortable: true,
        },
      ],
      confirmFields: [
        { key: "item_code", label: this.$t("Item Code") },
        { key: "item_description", label: this.$t("Description") },
        { key: "current_max", label: this.$t("Current Max") },
        { key: "suggested_max", label: this.$t("New Max") },
        { key: "current_reorder", label: this.$t("Current Reorder") },
        { key: "suggested_reorder", label: this.$t("New Reorder") },
      ],
      currentPage: 1,
      perPage: 50,
      perPageOptions: [20, 50, 100],
    };
  },
  computed: {
    supplierOptions() {
      // Get unique suppliers from items
      const suppliers = new Map();
      this.items.forEach((item) => {
        if (item.supplier_account_id && item.supplier_company_name) {
          suppliers.set(item.supplier_account_id, item.supplier_company_name);
        }
      });

      const options = [{ value: null, text: this.$t("All Suppliers") }];
      suppliers.forEach((name, id) => {
        options.push({ value: id, text: name });
      });

      return options.sort((a, b) => {
        if (a.value === null) return -1;
        if (b.value === null) return 1;
        return a.text.localeCompare(b.text);
      });
    },
    filteredItems() {
      let filtered = [...this.items];

      // Hide items with max stock = 0
      if (this.filters.hideZeroMax) {
        filtered = filtered.filter((item) => item.current_max_stock_value > 0);
      }

      // Supplier filter
      if (this.filters.supplierId) {
        filtered = filtered.filter(
          (item) => item.supplier_account_id === this.filters.supplierId
        );
      }

      // Search filter with SQL wildcard support (% and _)
      if (this.filters.search) {
        const searchPattern = this.convertSqlWildcardToRegex(
          this.filters.search
        );
        filtered = filtered.filter(
          (item) =>
            searchPattern.test(item.item_code) ||
            searchPattern.test(item.item_description)
        );
      }

      // Change type filter
      if (this.filters.changeType !== "all") {
        filtered = filtered.filter(
          (item) => item.change_needed === this.filters.changeType
        );
      }

      // Min change amount filter
      if (
        this.filters.minChangeAmount !== null &&
        this.filters.minChangeAmount !== ""
      ) {
        filtered = filtered.filter(
          (item) => Math.abs(item.change_amount) >= this.filters.minChangeAmount
        );
      }

      return filtered;
    },
    summaryStats() {
      // Apply hideZeroMax filter to summary stats to match what's filterable
      let itemsForStats = this.items;
      if (this.filters.hideZeroMax) {
        itemsForStats = itemsForStats.filter(
          (item) => item.current_max_stock_value > 0
        );
      }

      const total = itemsForStats.length;
      const increase = itemsForStats.filter(
        (item) => item.change_needed === "increase"
      ).length;
      const decrease = itemsForStats.filter(
        (item) => item.change_needed === "decrease"
      ).length;

      let avgChangePercent = 0;
      if (total > 0) {
        const totalChangePercent = itemsForStats.reduce((sum, item) => {
          if (item.current_max_stock_value > 0) {
            return (
              sum +
              Math.abs(
                (item.change_amount / item.current_max_stock_value) * 100
              )
            );
          }
          return sum;
        }, 0);
        avgChangePercent = Math.round(totalChangePercent / total);
      }

      return {
        totalItems: total,
        increaseCount: increase,
        decreaseCount: decrease,
        avgChangePercent,
      };
    },
    selectedItemsDetails() {
      return this.items
        .filter((item) => this.selectedItems.has(item.item_id))
        .map((item) => ({
          item_code: item.item_code,
          item_description: item.item_description,
          current_max: item.current_max_stock_value,
          suggested_max: item.suggested_max_stock_value,
          current_reorder: item.current_reorder_stock_value,
          suggested_reorder: item.suggested_reorder_stock_value,
        }));
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const items = await api.stockOptimization.analyze({
          period_days: this.selectedPeriod,
        });
        this.items = items || [];
      } catch (error) {
        console.error("Error loading stock optimization data:", error);
        this.$bvToast.toast(
          this.$t("Failed to load stock optimization data. Please try again."),
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      } finally {
        this.loading = false;
      }
    },
    onSuggestedMaxChange(item) {
      // Recalculate suggested reorder proportionally when max changes
      if (item.current_max_stock_value > 0) {
        const ratio =
          item.current_reorder_stock_value / item.current_max_stock_value;
        item.suggested_reorder_stock_value = Math.round(
          item.suggested_max_stock_value * ratio
        );
      }
      // Recalculate change amount
      item.change_amount =
        item.suggested_max_stock_value - item.current_max_stock_value;
      item.change_needed =
        item.change_amount > 0
          ? "increase"
          : item.change_amount < 0
          ? "decrease"
          : "none";
    },
    toggleSelection(itemId) {
      if (this.selectedItems.has(itemId)) {
        this.selectedItems.delete(itemId);
      } else {
        this.selectedItems.add(itemId);
      }
      // Force reactivity
      this.selectedItems = new Set(this.selectedItems);
    },
    selectAllFiltered() {
      this.filteredItems.forEach((item) => {
        this.selectedItems.add(item.item_id);
      });
      this.selectedItems = new Set(this.selectedItems);
    },
    clearSelection() {
      this.selectedItems.clear();
      this.selectedItems = new Set();
    },
    clearFilters() {
      this.filters = {
        search: "",
        changeType: "all",
        minChangeAmount: null,
        hideZeroMax: true,
        supplierId: null,
      };
    },
    confirmApplyChanges() {
      if (this.selectedItems.size === 0) {
        return;
      }
      this.$bvModal.show("confirmModal");
    },
    async applyChanges() {
      const updates = this.items
        .filter((item) => this.selectedItems.has(item.item_id))
        .map((item) => ({
          item_id: item.item_id,
          max_stock_value: item.suggested_max_stock_value,
          reorder_stock_value: item.suggested_reorder_stock_value,
        }));

      try {
        const result = await api.stockOptimization.applyUpdates(updates);

        this.$bvToast.toast(
          this.$t("Successfully updated {count} items", {
            count: result.updated_count || updates.length,
          }),
          {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          }
        );

        // Clear selection and reload data
        this.clearSelection();
        await this.loadData();
      } catch (error) {
        console.error("Error applying stock updates:", error);
        this.$bvToast.toast(
          this.$t("Failed to apply updates. Please try again."),
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
      }
    },
    rowClass(item) {
      if (!item) return "";
      if (item.change_needed === "increase") return "table-success-light";
      if (item.change_needed === "decrease") return "table-danger-light";
      return "";
    },
    getChangeBadgeVariant(changeNeeded) {
      if (changeNeeded === "increase") return "success";
      if (changeNeeded === "decrease") return "danger";
      return "secondary";
    },
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined) return "0";
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    convertSqlWildcardToRegex(pattern) {
      // Convert SQL LIKE wildcards to JavaScript RegExp
      // % = any characters (.*), _ = single character (.)

      // Escape special regex characters except % and _
      let escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      // Convert SQL wildcards to regex
      escaped = escaped
        .replace(/%/g, ".*") // % becomes .*
        .replace(/_/g, "."); // _ becomes .

      // Create case-insensitive regex
      return new RegExp(escaped, "i");
    },
  },
};
</script>

<style scoped>
.mini-stats-wid .card-body {
  padding: 1.25rem;
}

.table-success-light {
  background-color: rgba(52, 195, 143, 0.1);
}

.table-danger-light {
  background-color: rgba(244, 106, 106, 0.1);
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
